/**
 * Area-specific data preparation.
 *
 * The shared cartesian normalizer does the work both cartesian charts need
 * (see ../cartesianUtils). What is added here is everything that follows from
 * filling the space under the value rather than only tracing it:
 *
 * - a stack has to add up, so a missing value cannot stay a gap. It is counted
 *   as zero and reported, because leaving the hole would silently drop every
 *   band above it down to the baseline and restate the total
 * - the plotted numbers and the supplied numbers therefore stop being the same
 *   thing, so both are returned: the stack-safe copy is drawn, the original is
 *   what the table and tooltip quote
 * - a percent stack is a share of each point's own total, which is undefined
 *   where that total is zero and meaningless where it is negative
 * - the band at the bottom of a stack is the only one with a straight baseline,
 *   so which series gets it is a real choice rather than an accident of key order
 */

import {
  hasMixedSigns,
  normalizeSeriesData,
  type CartesianPoint,
  type ChartSeries,
  type SeriesInput,
} from "../cartesianUtils";

export type { ChartSeries, SeriesInput };
/** An area's point is the shared cartesian point under the area chart's name. */
export type AreaPoint = CartesianPoint;

/**
 * Plain areas sit on the baseline and may overlap. Stacked bands sit on each
 * other and sum to the total. Percent restates that stack as a share of each
 * point's own total, which trades the magnitude for the composition.
 */
export type AreaVariant = "area" | "stacked" | "percent";

/**
 * Which series lands on the baseline of a stack. The bottom band is the only
 * one whose shape can be read exactly, so "largest" puts the biggest series
 * there and gives the chart its steadiest floor.
 */
export type BandOrder = "given" | "largest" | "smallest";

export type PointSort = "none" | "asc" | "desc";

export interface NormalizedAreaData {
  /** Stack-safe values, drawn by the chart. */
  points: AreaPoint[];
  /** Values as supplied, quoted by the table and the tooltip. */
  rawPoints: AreaPoint[];
  /** In draw order: the first entry is the band on the baseline. */
  series: ChartSeries[];
  warnings: string[];
}

export interface NormalizeAreaOptions {
  xKey: string;
  series?: SeriesInput[];
  palette: readonly string[];
  maxSeries: number;
  sort: PointSort;
  variant: AreaVariant;
  bandOrder: BandOrder;
}

/** Sum of every series at one point, counting missing values as absent. */
function pointTotal(point: AreaPoint, series: ChartSeries[]): number {
  return series.reduce((sum, s) => {
    const value = point[s.key];
    return typeof value === "number" ? sum + value : sum;
  }, 0);
}

/** Total of one series across every point, used to order the bands. */
function seriesTotal(points: AreaPoint[], entry: ChartSeries): number {
  return points.reduce((sum, point) => {
    const value = point[entry.key];
    return typeof value === "number" ? sum + value : sum;
  }, 0);
}

/**
 * Order the bands of a stack.
 *
 * Colour was assigned by identity before this runs, so reordering moves the
 * bands without repainting them: a series keeps its hue wherever it lands.
 */
export function orderBands(
  points: AreaPoint[],
  series: ChartSeries[],
  order: BandOrder,
): ChartSeries[] {
  if (order === "given" || series.length < 2) return series;
  const ranked = [...series].sort((a, b) => {
    const cmp = seriesTotal(points, a) - seriesTotal(points, b);
    return order === "largest" ? -cmp : cmp;
  });
  return ranked;
}

/**
 * Prepare rows for the area chart: normalize, order the bands, and make the
 * values safe for the variant being drawn.
 *
 * Returns points that are always safe to render, the originals to quote, and
 * the notes a reader needs to trust the difference between them.
 */
export function normalizeAreaData(
  data: unknown,
  options: NormalizeAreaOptions,
): NormalizedAreaData {
  const { xKey, series: seriesInput, palette, maxSeries, sort, variant, bandOrder } = options;
  const stacks = variant !== "area";

  const normalized = normalizeSeriesData(data, {
    xKey,
    series: seriesInput,
    palette,
    maxSeries,
    sort,
    missingNote: stacks
      ? "counted as zero, because a stack has to add up"
      : "drawn as gaps in the area",
  });

  const warnings = [...normalized.warnings];
  const rawPoints = normalized.points;
  const series = orderBands(rawPoints, normalized.series, stacks ? bandOrder : "given");

  if (!stacks) {
    return { points: rawPoints, rawPoints, series, warnings };
  }

  if (series.length > 1 && hasMixedSigns(rawPoints, series)) {
    warnings.push(
      "This data mixes positive and negative values, which a stack cannot add up. Plain overlapping areas show it honestly.",
    );
  }

  // From here the drawn numbers and the supplied numbers part ways, so the
  // stack works on a copy and the originals stay intact for the readouts.
  const points: AreaPoint[] = rawPoints.map((point) => {
    const next: AreaPoint = { x: point.x, __source: point.__source };
    series.forEach((s) => {
      const value = point[s.key];
      next[s.key] = typeof value === "number" ? value : 0;
    });
    return next;
  });

  if (variant === "stacked") return { points, rawPoints, series, warnings };

  /* Percent: every point becomes a share of its own total. A point that sums to
     zero has no composition to show, and a negative share is not a quantity, so
     both are emptied rather than drawn as something they are not. */
  let emptyPoints = 0;
  let negatives = 0;

  points.forEach((point) => {
    series.forEach((s) => {
      const value = point[s.key];
      if (typeof value === "number" && value < 0) {
        negatives += 1;
        point[s.key] = 0;
      }
    });

    const total = pointTotal(point, series);
    if (total <= 0) {
      emptyPoints += 1;
      series.forEach((s) => {
        point[s.key] = null;
      });
      return;
    }

    series.forEach((s) => {
      const value = point[s.key];
      point[s.key] = typeof value === "number" ? (value / total) * 100 : null;
    });
  });

  if (negatives > 0) {
    warnings.push(
      `${negatives} negative ${negatives === 1 ? "value was" : "values were"} counted as zero. A share of a total cannot be negative.`,
    );
  }
  if (emptyPoints > 0) {
    warnings.push(
      `${emptyPoints} ${emptyPoints === 1 ? "point has" : "points have"} a total of zero, so ${emptyPoints === 1 ? "it has" : "they have"} no composition to show and ${emptyPoints === 1 ? "is" : "are"} left blank.`,
    );
  }

  return { points, rawPoints, series, warnings };
}
